import api from './api';

/**
 * #398: the insurer's own products (`insurers.InsurancePolicy`), backed by
 * `/insurers/insurance/policies/`. Edits go live on save (no approval step);
 * every write is a version the insurer can revert in one click.
 */

export const PRODUCTS_URL = '/insurers/insurance/policies/';

export type ProductStatus = 'draft' | 'active' | 'inactive' | 'discontinued';

export interface ProductRider {
  id?: string;
  name: string;
  code: string;
  rider_type: 'cover' | 'benefit' | 'service';
  irdai_rider_uin: string;
  description: string;
  percentage_of_premium: string | null;
  fixed_premium: string | null;
  sum_insured: string | null;
  is_mandatory: boolean;
  is_active: boolean;
}

export interface ProductServiceArea {
  id?: string;
  rule_type: 'allow' | 'exclude';
  state: string | null;
  state_name?: string | null;
  pincode: string;
  is_active?: boolean;
}

export interface RateChartSummary {
  id: string;
  label: string;
  chart_version: number;
  effective_from: string;
  row_count: number;
  is_current: boolean;
  uploaded_by_label: string;
  created_at: string;
  warnings?: string[];
}

export interface LivePolicyCount {
  count: number | null;
  precision: 'exact' | 'up_to' | 'unknown';
}

export interface ProductListRow {
  id: string;
  insurer_name: string;
  name: string;
  code: string;
  uin: string;
  category: string;
  category_ref_name: string | null;
  description?: string;
  features?: string[];
  status: ProductStatus;
  is_active: boolean;
  is_visible_to_all: boolean;
  min_premium: string | null;
  max_premium: string | null;
  sum_insured_min: string | null;
  sum_insured_max: string | null;
  version_no: number;
  row_version: number;
  updated_at: string;
}

export interface ProductDetail extends ProductListRow {
  product: string | null;
  product_name: string | null;
  short_description: string;
  seo_meta_title: string;
  seo_meta_description: string;
  seo_slug: string;
  exclusions: string;
  waiting_period: string;
  premium_frequency: string;
  min_age: number | null;
  max_age: number | null;
  max_renewal_age: number | null;
  eligibility_notes: string;
  wording_version: string;
  wording_effective_date: string | null;
  underwriting_guidelines: string;
  brochure_pdf: string | null;
  policy_wording_pdf: string | null;
  claim_form_pdf: string | null;
  sample_policy_pdf: string | null;
  product_page_url: string;
  claim_process_url: string;
  terms_url: string;
  tags: string[];
  withdrawn_on: string | null;
  riders: ProductRider[];
  service_areas: ProductServiceArea[];
  current_rate_chart: RateChartSummary | null;
  live_policy_count?: LivePolicyCount;
  activation_missing?: string[];
}

export interface ProductVersion {
  id: string;
  version_no: number;
  action:
    | 'create'
    | 'update'
    | 'status_change'
    | 'revert'
    | 'rate_chart'
    | 'broker_access'
    | 'delete';
  changed_by_display: string;
  changed_by_role: string;
  reason: string;
  changed_fields: string[];
  diff: Record<string, [unknown, unknown]>;
  reverted_from_version: number | null;
  created_at: string;
}

export interface CatalogueProduct {
  id: string;
  name: string;
  code: string;
  insurance_type_name: string | null;
  group_name: string | null;
}

export const STATUS_LABELS: Record<ProductStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  inactive: 'Inactive',
  discontinued: 'Discontinued',
};

/** Badge variant per status: Draft grey, Active green, Inactive amber, Discontinued red. */
export const STATUS_BADGE: Record<ProductStatus, 'default' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  active: 'success',
  inactive: 'warning',
  discontinued: 'error',
};

/** Fields whose change on an ACTIVE product needs the old -> new confirmation (spec 7.2). */
export const RISKY_FIELDS: Record<string, string> = {
  min_premium: 'Minimum premium',
  max_premium: 'Maximum premium',
  sum_insured_min: 'Minimum sum insured',
  sum_insured_max: 'Maximum sum insured',
  min_age: 'Minimum entry age',
  max_age: 'Maximum entry age',
  max_renewal_age: 'Renewal age limit',
  uin: 'UIN',
  service_areas: 'Location rules',
  policy_wording_pdf: 'Policy wording',
};

export const FIELD_LABELS: Record<string, string> = {
  ...RISKY_FIELDS,
  name: 'Name',
  code: 'Code',
  category: 'Category',
  category_ref: 'Category',
  product: 'Catalogue product',
  description: 'Description',
  short_description: 'Short description',
  premium_frequency: 'Premium frequency',
  eligibility_notes: 'Eligibility notes',
  wording_version: 'Wording version',
  wording_effective_date: 'Wording effective date',
  underwriting_guidelines: 'Underwriting guidelines',
  brochure_pdf: 'Brochure',
  claim_form_pdf: 'Claim form',
  sample_policy_pdf: 'Sample policy',
  riders: 'Riders',
  rate_chart: 'Rate chart',
  status: 'Status',
  broker_access: 'Broker access',
  is_visible_to_all: 'Visible to all brokers',
  is_deleted: 'Deleted',
};

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** Short display of one history value. */
export function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '(empty)';
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('broker_name' in obj) return `${obj.broker_name}: ${obj.new}`;
    return JSON.stringify(value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const text = String(value);
  // A stored file path: show only the file name.
  return text.includes('/') ? text.split('/').pop() || text : text;
}

/** The risky changes between the loaded product and the form, as old -> new rows. */
export function riskyChanges(
  before: Record<string, unknown>,
  after: Record<string, unknown>
): { field: string; label: string; from: string; to: string }[] {
  const out: { field: string; label: string; from: string; to: string }[] = [];
  for (const [field, label] of Object.entries(RISKY_FIELDS)) {
    if (!(field in after)) continue;
    const a = normalise(before[field]);
    const b = normalise(after[field]);
    if (a !== b)
      out.push({ field, label, from: displayValue(before[field]), to: displayValue(after[field]) });
  }
  return out;
}

function normalise(value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value)) return String(Number(value));
  if (value instanceof File) return `file:${value.name}`;
  return JSON.stringify(value);
}

export function livePolicyText(live: LivePolicyCount | undefined): string {
  if (!live || live.precision === 'unknown' || live.count === null) {
    return 'The number of issued policies on this product is unknown (no catalogue mapping).';
  }
  const n = live.count.toLocaleString('en-IN');
  const prefix = live.precision === 'up_to' ? 'Up to ' : '';
  return `${prefix}${n} issued polic${live.count === 1 ? 'y uses' : 'ies use'} this product.`;
}

/**
 * The shared axios instance defaults to `Content-Type: application/json`, and
 * axios then flattens a FormData body into a JSON object (every value a string:
 * "" for empty numbers and dates, "[]" for riders). The product API reads the
 * body as multipart (files travel with it), so say so explicitly.
 */
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

export const insurerProductsService = {
  get: async (id: string) => (await api.get<ProductDetail>(`${PRODUCTS_URL}${id}/`)).data,
  create: async (body: FormData) => (await api.post<ProductDetail>(PRODUCTS_URL, body, MULTIPART)).data,
  update: async (id: string, body: FormData) =>
    (await api.patch<ProductDetail>(`${PRODUCTS_URL}${id}/`, body, MULTIPART)).data,
  setStatus: async (
    id: string,
    body: {
      status: ProductStatus;
      reason?: string;
      confirm_live_policies?: boolean;
      row_version?: number;
    }
  ) => (await api.post<ProductDetail>(`${PRODUCTS_URL}${id}/set_status/`, body)).data,
  revert: async (
    id: string,
    body: { version_no?: number; reason?: string; skip_conflicts?: boolean }
  ) => (await api.post<ProductDetail>(`${PRODUCTS_URL}${id}/revert/`, body)).data,
  versions: async (id: string) => {
    const data = (await api.get(`${PRODUCTS_URL}${id}/versions/`, { params: { page_size: 100 } }))
      .data;
    return (Array.isArray(data) ? data : (data.results ?? [])) as ProductVersion[];
  },
  remove: async (id: string) => api.delete(`${PRODUCTS_URL}${id}/`),
  catalogue: async () =>
    (await api.get<CatalogueProduct[]>(`${PRODUCTS_URL}catalogue_products/`)).data,
  rateCharts: async (id: string) =>
    (await api.get<RateChartSummary[]>(`${PRODUCTS_URL}${id}/rate_charts/`)).data,
  uploadRateChart: async (id: string, body: FormData) =>
    (await api.post<RateChartSummary>(`${PRODUCTS_URL}${id}/rate_charts/`, body)).data,
  /** Rate charts are insurer-confidential: fetched with the session, never a public link. */
  downloadRateChart: (id: string, chartId: string, filename: string) =>
    downloadBlob(`${PRODUCTS_URL}${id}/rate_charts/${chartId}/download/`, filename),
  downloadTemplate: (id: string) =>
    downloadBlob(`${PRODUCTS_URL}${id}/rate_charts/template/`, 'rate-chart-template.csv'),
};

async function downloadBlob(url: string, filename: string) {
  const response = await api.get(url, { responseType: 'blob' });
  const href = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
}
